import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DRIVER_CREATE, DRIVER_DELETE, DRIVERS_FETCH, PREFERENCE_LOCATION_SET, ROUTER_NAVIGATION } from '../action-types';
import { from, mergeMap, switchMap, withLatestFrom } from 'rxjs';
import { IAppState } from '../app/app-state';
import { getLocationPreference } from '../preference/preference-selectors';
import { LocationPreference } from '@core/models/preference/location-preference';
import { UserDataService } from '@core/data/api/user/user-data.service';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';
import { DRIVER_ENTITY } from '../entity-types';
import { User } from '@core/models/user/user';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { schema } from 'normalizr';
import { CreateDriverAction, DeleteDriverAction, FetchDriversAction, SetDriverAction, SetDriversAction, SetDriversFetchingAction, UnsetDriverAction } from './drivers-actions';

@Injectable()
export class DriversEffects {

    constructor(private userDataService: UserDataService,
                private store$: Store<IAppState>,
                private actions$: Actions) {
    }

    fetchDrivers$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchDriversAction>(DRIVERS_FETCH),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(
                ([action, locationPreference]: [FetchDriversAction, LocationPreference]) => {

                    const locationId: string = locationPreference.id;

                    return this.userDataService.fetchIHDDrivers(locationId)
                        .pipe(
                            ossNormalize(DRIVER_ENTITY, User, 'drivers'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetDriversAction(normalized),
                                    new SetDriversFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions]) =>
                                from(actions.concat([new SetDriversFetchingAction(false)]))
                            )
                        );
                }
            )
        )
    );

    createDriver$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateDriverAction>(DRIVER_CREATE),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [CreateDriverAction, LocationPreference]) => {

                return this.userDataService.createIHDDriver({ 
                        ...action.payload.request,
                        location_id: locationPreference.id
                    })
                    .pipe(
                        mergeMap((response) => {

                            return [
                                (action as any).success(response),
                                new SetDriverAction({
                                    item: Object.assign(new User(), response.driver),
                                    schema: new schema.Entity(DRIVER_ENTITY)
                                })
                                
                            ];
                        }),
                        ossCatchError(([errorActions, error]) =>
                            from(errorActions.concat([(action as any).error(error)]))
                        )
                    );
            })
        )
    );

    deleteDriver$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DeleteDriverAction>(DRIVER_DELETE),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [DeleteDriverAction, LocationPreference]) => {

                return this.userDataService.deleteIHDDriver(action.payload.driverId, locationPreference.id)
                    .pipe(
                        mergeMap((response) => {
                            return [
                                (action as any).success(response),
                                new UnsetDriverAction({ id: action.payload.driverId })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) =>
                            from(errorActions.concat([(action as any).error(error)]))
                        )
                    );
            })
        )
    );
}
